import { FC, useState } from 'react';

import pluralize from 'pluralize';
import { GATHERING_TERM_PLURAL } from 'src/constants/Connections';

import { SocialCircle, useUserSocialCirclesQuery } from '@queries/UserSocialCircleQueries';

import { useAnalytics } from '@hooks/useAnalytics';

import { useNavigate } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { LineAwesomeIcon } from '@components/icons/LineAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { List } from '@components/layout/List/List';
import { Screen } from '@components/layout/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { SocialCircleInquiryPopup } from './components/SocialCircleInquiryPopup';

const SocialCircleCardHeaderActionButton: FC<{ socialCircle: SocialCircle }> = ({
  socialCircle,
}) => {
  return (
    <IconButton variant="smooth" color="neutral" onClick={() => {}}>
      <LineAwesomeIcon name="ellipsis-h" />
    </IconButton>
  );
};

const SocialCircleCard: FC<{ socialCircle: SocialCircle; onInquire: () => void }> = ({
  socialCircle,
  onInquire,
}) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();

  const handleInquire = () => {
    track('Inquire Social Circle', { socialCircleId: socialCircle._id });
    onInquire();
  };

  const handleViewInquiry = (inquiry: SocialCircle['upcomingInquiries'][number]) => {
    navigate(`/noauth/social-circle-inquiries/${inquiry._id}`, {
      state: { back: '/manage/circles' },
    });
  };

  return (
    <Card>
      <Card.Header
        title={socialCircle.name}
        subtitle={
          `${socialCircle.acceptedMembershipCount} ${pluralize('member', socialCircle.acceptedMembershipCount)}` +
          (socialCircle.pendingMembershipCount > 0
            ? `, ${socialCircle.pendingMembershipCount} pending`
            : '')
        }
        action={<SocialCircleCardHeaderActionButton socialCircle={socialCircle} />}
      />
      {socialCircle?.upcomingInquiries?.length > 0 ? (
        <Card.Body className="py-0 mb-3" style={{ background: `rgba(0, 0, 0, 0.3)` }}>
          <List
            className="my-0"
            ignoreScreenContentPadding
            listItems={socialCircle.upcomingInquiries.map((inquiry) => ({
              title: inquiry.title ?? 'Untitled Inquiry',
              subTitle: `${1} going, ${0} maybe`,
              onClick: () => {
                handleViewInquiry(inquiry);
              },
            }))}
          />
        </Card.Body>
      ) : (
        <Card.Body>
          <EmptyState
            title={`No upcoming ${GATHERING_TERM_PLURAL}`}
            text={`This social circle has no upcoming ${GATHERING_TERM_PLURAL}. Interested in seeing who's interest in something coming up? Put out a feeler and see who's interested.`}
          />
        </Card.Body>
      )}
      <Card.Footer>
        <Button roundness="rounded" onClick={handleInquire}>
          Put out a feeler
        </Button>
      </Card.Footer>
    </Card>
  );
};

const SocialCircleList: FC<{ socialCircles: SocialCircle[] }> = ({ socialCircles }) => {
  return (
    <List
      className="mt-0"
      ignoreScreenContentPadding
      listItems={socialCircles.map((circle) => ({
        title: circle.name,
        subTitle:
          `${circle.acceptedMembershipCount} ${pluralize('member', circle.acceptedMembershipCount)}` +
          (circle.pendingMembershipCount > 0 ? `, ${circle.pendingMembershipCount} pending` : ''),
      }))}
    />
  );
};

export const SocialCirclesContent: FC = () => {
  const [currentInquirySocialCircleId, setCurrentInquirySocialCircleId] = useState<string>();
  const { socialCircles } = useUserSocialCirclesQuery();

  return (
    <>
      {socialCircles.map((socialCircle) => (
        <SocialCircleCard
          key={socialCircle._id}
          socialCircle={socialCircle}
          onInquire={() => setCurrentInquirySocialCircleId(socialCircle._id)}
        />
      ))}
      <SocialCircleInquiryPopup
        isVisible={!!currentInquirySocialCircleId}
        onClose={() => setCurrentInquirySocialCircleId(undefined)}
        socialCircleId={currentInquirySocialCircleId}
      />
    </>
  );
};

export const SocialCirclesScreen = createScreenComponent(() => {
  return (
    <Screen
      name="Social Circles Screen"
      headerProps={{
        left: <></>,
        title: <Screen.GagglTitle />,
      }}
    >
      <Screen.Content>
        <TitleToolbar text="Social Circles" />
        <SocialCirclesContent />
      </Screen.Content>
    </Screen>
  );
});
